'use client'

import { CircleCheckIcon, Zap, Gift } from 'lucide-react'
import { useTranslations } from 'next-intl'

/**
 * Simplified Understanding AI Credits section - focused on core information
 */
export function UnderstandingCredits() {
  const t = useTranslations('pricing-v3')

  return (
    <section className="container mx-auto px-4 py-16">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {t('understanding_credits.title')}
          </h2>
          <p className="text-white/70 text-base max-w-2xl mx-auto">
            {t('understanding_credits.subtitle')}
          </p>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {/* What are AI Credits - Single line */}
          <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-2xl p-6">
            <p className="text-white/90 text-lg leading-relaxed text-center">
              {t('understanding_credits.what_are.description')}
            </p>
          </div>

          {/* Two columns: Uses Credits vs Free */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Uses Credits */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-5 h-5 text-yellow-400" />
                <h3 className="text-xl font-bold text-white">
                  {t('understanding_credits.when_use.ai_tasks_title')}
                </h3>
              </div>
              <div className="space-y-2.5 text-sm text-white/80">
                <div className="flex items-start gap-2">
                  <span className="text-yellow-400 mt-0.5">•</span>
                  <span>{t('understanding_credits.when_use.ai_task_1')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-yellow-400 mt-0.5">•</span>
                  <span>{t('understanding_credits.when_use.ai_task_2')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-yellow-400 mt-0.5">•</span>
                  <span>{t('understanding_credits.when_use.ai_task_4')}</span>
                </div>
              </div>
            </div>

            {/* Free Actions */}
            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Gift className="w-5 h-5 text-green-400" />
                <h3 className="text-xl font-bold text-white">
                  {t('understanding_credits.when_use.quick_edits_title')}
                </h3>
              </div>
              <div className="space-y-2.5 text-sm text-white/80">
                <div className="flex items-start gap-2">
                  <CircleCheckIcon className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>
                    {t('understanding_credits.when_use.quick_edit_1')}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <CircleCheckIcon className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>
                    {t('understanding_credits.when_use.quick_edit_2')}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <CircleCheckIcon className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <span>
                    {t('understanding_credits.when_use.quick_edit_4')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* How Much You Need - 3 examples only */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4 text-center">
              {t('understanding_credits.how_much.title')}
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {/* Small Project */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-semibold text-base">
                    {t('understanding_credits.how_much.projects.resume.title')}
                  </span>
                  <span className="text-[#FDD577] font-bold text-sm whitespace-nowrap">
                    ~200
                  </span>
                </div>
                <p className="text-white/60 text-xs">
                  {t(
                    'understanding_credits.how_much.projects.resume.description',
                  )}
                </p>
              </div>

              {/* Medium Project */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-semibold text-base">
                    {t(
                      'understanding_credits.how_much.projects.ai_startup.title',
                    )}
                  </span>
                  <span className="text-[#FDD577] font-bold text-sm whitespace-nowrap">
                    ~1,000
                  </span>
                </div>
                <p className="text-white/60 text-xs">
                  {t(
                    'understanding_credits.how_much.projects.ai_startup.description',
                  )}
                </p>
              </div>

              {/* Large Project */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-semibold text-base">
                    {t('understanding_credits.how_much.projects.saas.title')}
                  </span>
                  <span className="text-[#FDD577] font-bold text-sm whitespace-nowrap">
                    5,000+
                  </span>
                </div>
                <p className="text-white/60 text-xs">
                  {t(
                    'understanding_credits.how_much.projects.saas.description',
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
